import { Injectable } from '@angular/core';
import { T685 } from '@asm/features/sync/models/pricing-tables.model';
import { LocalConditionResult, ManualCondition, PricingResult, PricingStep } from '@asm/features/sync/services/pricing/pricing.interfaces';

@Injectable({ providedIn: 'root' })
export class DefaultLocalPricingCalculationService {
  private static readonly INTERMEDIATE_DECIMAL_PLACES = 4;
  private static readonly FINAL_DECIMAL_PLACES = 2;

  async calculate(
    conditionRecords: Map<string, LocalConditionResult>,
    context: Record<string, any> = {},
    manualConditions: ManualCondition[] = [],
    allConditions: T685[] = [],
    qty: number = 1,
    currency: string = 'BRL',
  ): Promise<PricingResult> {
    const safeQty = qty > 0 ? qty : 1;
    const steps: PricingStep[] = [];
    const appliedManualKeys = new Set(manualConditions.map(condition => condition.condition));
    const appliedAutomaticKeys = new Set<string>();

    for (const [conditionKey, result] of conditionRecords.entries()) {
      const definition = this.findConditionDefinition(allConditions, conditionKey);

      if (!definition || definition.calculationLevel == null) {
        continue;
      }

      appliedAutomaticKeys.add(definition.KSCHL);

      steps.push({
        stepNumber: definition.calculationLevel,
        condition: definition.KSCHL,
        description: definition.VTEXT,
        baseValue: 0,
        conditionRate: this.roundIntermediate(Number(result.register?.KBETR ?? 0)),
        conditionUnit: definition.KRECH === 'A' ? '%' : currency,
        calculatedAmount: 0,
        resultValue: 0,
        conditionClass: definition.KOAID,
        calculationType: definition.KRECH,
        isSubtotal: Boolean(definition.KZUST),
        isManual: false,
        isApplied: true,
        baseConditionKey: definition.baseCondition ?? null,
        accessSequence: result.sequence,
        conditionTable: result.table,
        conditionRecord: result.register ?? null,
      });
    }

    for (const manualCondition of manualConditions) {
      const definition = this.findConditionDefinition(allConditions, manualCondition.condition);

      if (!definition || definition.calculationLevel == null) {
        continue;
      }

      steps.push({
        stepNumber: definition.calculationLevel,
        condition: definition.KSCHL,
        description: definition.VTEXT ?? manualCondition.description ?? definition.KSCHL,
        baseValue: 0,
        conditionRate: this.roundIntermediate(Number(manualCondition.conditionRate ?? 0)),
        conditionUnit: definition.KRECH === 'A' ? '%' : currency,
        calculatedAmount: 0,
        resultValue: 0,
        conditionClass: definition.KOAID ?? manualCondition.conditionClass,
        calculationType: definition.KRECH ?? manualCondition.calculationType,
        isSubtotal: Boolean(definition.KZUST),
        // Mirrors online DefaultPricingCalculationService:
        // promotion-derived manual conditions have isManual=false so the frontend
        // identifies them as removable promotions (parseConditionSteps checks isManual).
        isManual: definition.isPromotion ? false : true,
        isApplied: true,
        baseConditionKey: definition.baseCondition ?? manualCondition.baseConditionKey ?? null,
        accessSequence: null,
        conditionTable: null,
        conditionRecord: null,
      });
    }

    for (const condition of allConditions) {
      if (
        condition.calculationLevel == null ||
        appliedAutomaticKeys.has(condition.KSCHL) ||
        appliedManualKeys.has(condition.KSCHL)
      ) {
        continue;
      }

      steps.push({
        stepNumber: condition.calculationLevel,
        condition: condition.KSCHL,
        description: condition.VTEXT,
        baseValue: 0,
        conditionRate: 0,
        conditionUnit: condition.KRECH === 'A' ? '%' : currency,
        calculatedAmount: 0,
        resultValue: 0,
        conditionClass: condition.KOAID,
        calculationType: condition.KRECH,
        isSubtotal: Boolean(condition.KZUST),
        isManual: Boolean(condition.isManualEntry),
        isApplied: false,
        baseConditionKey: condition.baseCondition ?? null,
        accessSequence: null,
        conditionTable: null,
        conditionRecord: null,
      });
    }

    steps.sort((left, right) => left.stepNumber - right.stepNumber || left.condition.localeCompare(right.condition));

    const stepResults = new Map<string, number>();
    let currentTotal = 0;

    for (const step of steps) {
      if (!step.isApplied) {
        step.baseValue = 0;
        step.conditionRate = 0;
        step.calculatedAmount = 0;
        step.resultValue = this.round(currentTotal, 4);
        continue;
      }

      let baseValue = currentTotal;
      if (step.baseConditionKey) {
        const baseResult = stepResults.get(step.baseConditionKey);
        const baseStep = steps.find(item => item.condition === step.baseConditionKey);

        if (baseResult !== undefined && baseStep?.isSubtotal) {
          baseValue = baseResult;
        }
      }

      step.baseValue = this.roundIntermediate(baseValue);

      if (step.isSubtotal) {
        step.calculatedAmount = 0;
        step.resultValue = this.roundIntermediate(baseValue);
        stepResults.set(step.condition, step.resultValue);
        currentTotal = step.resultValue;
        continue;
      }

      this.applyBuiltinCalculation(step, baseValue, safeQty);
      step.calculatedAmount = this.roundIntermediate(step.calculatedAmount);
      step.resultValue = this.roundIntermediate(step.resultValue);

      stepResults.set(step.condition, step.resultValue);
      currentTotal = step.resultValue;
    }

    const appliedSteps = steps.filter(step => step.isApplied);
    const basePrice = appliedSteps.length > 0 ? appliedSteps[0].resultValue : 0;

    let totalDiscounts = 0;
    let totalSurcharges = 0;
    let totalTaxes = 0;
    let discountPercent = 0;
    let surchargePercent = 0;
    let st = 0;
    let ipi = 0;
    let icms = 0;

    for (const step of appliedSteps) {
      const definition = this.findConditionDefinition(allConditions, step.condition);

      if (step.conditionClass === 'A' && definition?.includeInTotalDiscount) {
        totalDiscounts += step.calculatedAmount;

        if (step.calculationType === 'A') {
          discountPercent += step.conditionRate;
        } else if (basePrice > 0) {
          discountPercent += (step.calculatedAmount / basePrice) * 100;
        }
      } else if (step.conditionClass === 'B' && definition?.includeInTotalSurcharge) {
        totalSurcharges += step.calculatedAmount;

        if (step.calculationType === 'A') {
          surchargePercent += step.conditionRate;
        } else if (basePrice > 0) {
          surchargePercent += (step.calculatedAmount / basePrice) * 100;
        }
      } else if (step.conditionClass === 'C') {
        totalTaxes += step.calculatedAmount;
        if (step.condition === 'BX41') st += step.calculatedAmount;
        if (step.condition === 'BX23') ipi += step.calculatedAmount;
        if (step.condition === 'BX13') icms += step.calculatedAmount;
      }
    }

    const baseUnit = this.roundFinal(basePrice / safeQty);
    const discountUnit = this.roundFinal(totalDiscounts / safeQty);
    const surchargeUnit = this.roundFinal(totalSurcharges / safeQty);
    const taxUnit = this.roundFinal(totalTaxes / safeQty);
    const finalUnit = this.roundFinal(currentTotal / safeQty);
    const baseTotal = this.roundFinal(baseUnit * qty);
    const totalDiscount = this.roundFinal(discountUnit * qty);
    const totalSurcharge = this.roundFinal(surchargeUnit * qty);
    const totalTax = this.roundFinal(taxUnit * qty);
    const finalTotal = this.roundFinal(finalUnit * qty);
    const stUnit = this.roundFinal(st / safeQty);
    const ipiUnit = this.roundFinal(ipi / safeQty);
    const icmsUnit = this.roundFinal(icms / safeQty);

    return {
      qty,
      baseValue: baseUnit,
      baseTotal,
      totalValue: this.roundFinal(finalTotal - totalTax),
      finalPrice: finalUnit,
      finalTotal,
      discount: discountUnit,
      totalDiscount,
      discountPercent: this.roundFinal(discountPercent),
      surcharge: surchargeUnit,
      totalSurcharge,
      surchargePercent: this.roundFinal(surchargePercent),
      tax: taxUnit,
      totalTax,
      st: stUnit,
      ipi: ipiUnit,
      icms: icmsUnit,
      calculationDate: new Date(),
      context,
      formatedPrice: this.formatCurrency(baseUnit, currency),
      formatedFinalPrice: this.formatCurrency(finalUnit, currency),
      formatedDiscount: this.formatCurrency(discountUnit, currency),
      formatedTotalDiscount: this.formatCurrency(totalDiscount, currency),
      formatedSurcharge: this.formatCurrency(surchargeUnit, currency),
      formatedTotalSurcharge: this.formatCurrency(totalSurcharge, currency),
      formatedST: this.formatCurrency(stUnit, currency),
      formatedIPI: this.formatCurrency(ipiUnit, currency),
      formatedICMS: this.formatCurrency(icmsUnit, currency),
      currency,
      steps,
    };
  }

  private applyBuiltinCalculation(step: PricingStep, baseValue: number, qty: number): void {
    if (step.calculationType === 'A') {
      step.calculatedAmount = (baseValue * step.conditionRate) / 100;
    } else if (step.calculationType === 'B') {
      step.calculatedAmount = step.conditionRate * qty;
    } else {
      step.calculatedAmount = 0;
    }

    if (step.conditionClass === 'A') {
      step.resultValue = baseValue - step.calculatedAmount;
    } else {
      step.resultValue = baseValue + step.calculatedAmount;
    }
  }

  private findConditionDefinition(conditions: T685[], conditionKey: string): T685 | undefined {
    return (
      conditions.find(condition => condition.KSCHL === conditionKey) ??
      conditions.find(condition => condition.KOZGF === conditionKey)
    );
  }

  private formatCurrency(value: number, currency: string): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(value);
  }

  private round(value: number, digits: number): number {
    return Number.parseFloat(Number(value || 0).toFixed(digits));
  }

  private roundIntermediate(value: number): number {
    return this.round(value, DefaultLocalPricingCalculationService.INTERMEDIATE_DECIMAL_PLACES);
  }

  private roundFinal(value: number): number {
    return this.round(value, DefaultLocalPricingCalculationService.FINAL_DECIMAL_PLACES);
  }
}
